import { axiosInstance } from "../../../api/axiosInstance.tsx";

// Create a cache for permissions to avoid repeated API calls
let permissionCache: Record<string, string> | null = null;

interface PermissionOption {
  value: string; // Permission ID
  label: string; // Human-readable name (e.g., "Create User")
  name: string; // Original API name (e.g., "CREATE_USER")
}

// Update the interface to match your API
export interface roleSearchParams {
  name?: string;
  description?: string;
  limit?: number;
  offset?: number;
  keyword?: string;
  orderBy?: string;
  order?: string;
}

// In getPermissionOptions
export const getPermissionOptions = async (): Promise<PermissionOption[]> => {
  try {
    const response = await axiosInstance.get("/rbac/permissions");
    return response.data.map((permission: any) => ({
      value: permission.id,
      label: permission.name
        .replace(/_/g, " ") // Change underscores to spaces
        .replace(/\b\w/g, (l) => l.toUpperCase()), // Capitalize each word
      name: permission.name, // Keep the original API name
    }));
  } catch (error) {
    console.error("Failed to fetch permission options:", error);
    return [];
  }
};

// Fungsi untuk mendapatkan permission label (sudah ada, tapi ditampilkan untuk konteks)
export const getPermissionLabel = async (value: string): Promise<string> => {
  try {
    if (!permissionCache) {
      const response = await axiosInstance.get("/rbac/permissions");
      permissionCache = response.data.reduce(
        (acc: Record<string, string>, permission: any) => {
          acc[permission.id] = permission.name;
          return acc;
        },
        {}
      );
    }
    return permissionCache?.[value] || value;
  } catch (error) {
    console.error("Failed to fetch permissions:", error);
    return value;
  }
};

// Create role (without permissions)
export const createRole = async (roleData: {
  roleName: string;
  description: string;
}) => {
  if (!roleData.roleName) {
    throw new Error("Role name is required");
  }

  try {
    const response = await axiosInstance.post("/rbac/create-role", {
      name: roleData.roleName,
      description: roleData.description,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to create role";
  }
};

// Assign multiple permissions to role
export const assignPermissionsToRole = async (
  roleName: string,
  permissionNames: string[]
) => {
  if (!roleName || permissionNames.length === 0) return;

  try {
    await axiosInstance.post("/rbac/assign-permission", {
      roleName,
      permissionNames,
    });
  } catch (error) {
    console.error("Failed to assign permissions:", error);
    throw error.response?.data?.message || "Failed to assign permissions";
  }
};

// Add this new function for fetching all roles
export const getAllRoles = async (params: roleSearchParams = {}) => {
    try {
    const response = await axiosInstance.get("/rbac/roles", {
        params,
    });
    return response.data;
} catch (error: any) {
    throw new Error(error.response?.data?.message || "Failed to fetch roles");
  }
};

export const getRoles = async (params: roleSearchParams = {}) => {
    try {
    const response = await axiosInstance.get("/rbac/roles/search", {
        params,
    });
    return response.data;
} catch (error: any) {
    throw new Error(error.response?.data?.message || "Failed to fetch roles");
  }
};

// In roleService.tsx, update getRolePermissions to return permission names in consistent format
export const getRolePermissions = async (
    roleName: string
  ): Promise<{
    role: string;
    permissions: string[]; // Ensure this returns permission names (e.g., "CREATE_USER")
  }> => {
    try {
      const response = await axiosInstance.get(
        `/rbac/role-permissions/${roleName}`
      );
      // Ensure permissions are in the same format as your permissionOptions
      return {
        role: response.data.role,
        permissions: response.data.permissions.map((p: any) => p.name || p) // Adjust based on your API response
      };
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch role permissions"
      );
    }
  };

export const createPermission = async (permissionName: string) => {
  // Only call API if permissionName is provided
  if (!permissionName) return null;

  try {
    const response = await axiosInstance.post("/rbac/create-permission", {
      name: permissionName,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to create permission";
  }
};

export const deleteRole = async (id: string) => {
  try {
    await axiosInstance.delete(`/roles/delete_role/${id}`);
  } catch (error) {
    throw error.response?.data?.message || "Failed to delete role";
  }
};

// Add this function to roleService.tsx
export const updateRolePermission = async (
  roleName: string,
  oldPermissionName: string,
  newPermissionName: string
): Promise<any> => {
  try {
    const response = await axiosInstance.put("/rbac/change-role-permission", {
      roleName,
      oldPermissionName,
      newPermissionName,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to update role permission";
  }
};

export const updateRole = async (
    roleName: string,
    permissionNames: string[]
) => {
  try {
    const response = await axiosInstance.post("/rbac/assign-permission", {
        roleName,
        permissionNames,
  });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to update role";
  }
};

// Add this function to roleService.tsx for removing permissions
export const removePermissionFromRole = async (
  roleName: string,
  permissionName: string
) => {
  try {
    const response = await axiosInstance.post("/rbac/remove-permission", {
      roleName,
      permissionName,
    });
    return response.data;
  } catch (error) {
    throw (
      error.response?.data?.message || "Failed to remove permission from role"
    );
  }
};

// Add this to roleService.tsx
export const fetchRoleDetails = async (
  roleName: string
): Promise<{
  roleName: string;
  description: string;
  permissions: string[];
}> => {
  try {
    // Fetch both permissions and role details in parallel
    const [permissionsData, roleDetails] = await Promise.all([
      getRolePermissions(roleName),
      axiosInstance.get(`/rbac/role-permissions/${roleName}`),
    ]);

    return {
      roleName: roleName,
      description: roleDetails.data.description || "",
      permissions: permissionsData.permissions || [],
    };
  } catch (error) {
    console.error("Failed to fetch role data:", error);
    throw error;
  }
};
